import { UPLOADS_PUBLIC_PATH } from '@cult/schemas';

export { stripMarkdown } from '@cult/domain';

const SAFE_SCHEMES = ['http:', 'https:', 'mailto:'];
const HAS_SCHEME_RE = /^[a-zA-Z][a-zA-Z0-9+.-]*:/;

/**
 * Normalises a user-supplied href before rendering:
 * - bare domains / paths without a scheme → prepend https://
 * - protocol-relative (//…) → left unchanged so isSafeHref can reject them
 * - already has a scheme → returned as-is
 */
export function normalizeHref(href: string | undefined | null): string {
  if (typeof href !== 'string') return '';
  const trimmed = href.trim();
  if (
    trimmed.length === 0 ||
    trimmed.startsWith('//') ||
    trimmed.startsWith('#') ||
    trimmed.startsWith('/') ||
    trimmed.startsWith('./') ||
    trimmed.startsWith('../') ||
    HAS_SCHEME_RE.test(trimmed)
  )
    return trimmed;
  return `https://${trimmed}`;
}

export function isSafeHref(href: string | undefined | null): boolean {
  if (typeof href !== 'string') return false;
  const trimmed = href.trim();
  if (trimmed.length === 0) return false;
  if (trimmed.startsWith('//')) return false;
  const match = HAS_SCHEME_RE.exec(trimmed);
  if (!match) return false;
  return SAFE_SCHEMES.includes(match[0].toLowerCase());
}

const SAFE_IMG_SCHEMES = ['http:', 'https:'];

/**
 * Image-src guard: allows http(s) URLs and relative paths under
 * `UPLOADS_PUBLIC_PATH` (the API's authenticated file proxy). Everything else is
 * rejected so external/unsafe schemes can't sneak in via paste or
 * malformed markdown.
 */
export function isSafeImgSrc(src: string | undefined | null): boolean {
  if (typeof src !== 'string') return false;
  const trimmed = src.trim();
  if (trimmed.length === 0) return false;
  if (trimmed.startsWith(`${UPLOADS_PUBLIC_PATH}/`) && !trimmed.startsWith(`${UPLOADS_PUBLIC_PATH}//`)) return true;
  if (trimmed.startsWith('//')) return false;
  const match = HAS_SCHEME_RE.exec(trimmed);
  if (!match) return false;
  return SAFE_IMG_SCHEMES.includes(match[0].toLowerCase());
}
